/** Authored Hero Forge reference port from fable-rogue 03a219c0
 * tools/character-builder/src/. Numerical keyframes/curves are preserved.
 * See PROVENANCE.md for the explicit skeletal adapter boundary. */
import {type Quat,type V3,clamp,mix,normalize,qAxis,qConj,qEuler,qFromTo,qMul,qRotate,qSlerp,smoothstep} from './math';
import {B,type Pose} from './rig';
import type {Screen,Shot,Target} from './supers';
export type WeaponId='sword'|'spear'|'crystalStaff'|'lanternStaff';
export interface ClipContext {weapon:WeaponId|null;dashDistance:number;legs?:[number,number];lowGuard?:boolean}
export interface ClipEvents {ghost:boolean;dust:number;burst:boolean;charge:number;trail:number;target?:Target;sheath?:number;camera?:Shot;screen?:Screen}
export const noEvents=():ClipEvents=>({ghost:false,dust:0,burst:false,charge:0,trail:0});
export const rot=(pose:Pose,bone:number,x:number,y=0,z=0)=>{pose.rot[bone]=qEuler(x,y,z);};
const ease=(t:number)=>t*t*(3-2*t);
const legsOf=(ctx:ClipContext)=>ctx.legs??[.21,.20];
// Secondary expression/ears/tail remain owned by the target GLB rig. Source
// combat keyframes are unmodified; model-specific mood/catalog code is omitted.
export function secondary(_pose:Pose,_time:number,_ctx:ClipContext,_energy:number):void{}
interface Hold { upper: V3; fore: number; aim: V3; roll: number }

/** How each weapon is presented while standing, as on the sheet: staffs planted out to the side, the sword raised. */
export const HOLDS: Record<WeaponId, Hold> = {
  spear: { upper: [-0.36, 0, 1.0], fore: -0.8, aim: [0.2, 1, 0.05], roll: 0 },
  crystalStaff: { upper: [-0.32, 0, 0.88], fore: -0.92, aim: [0.18, 1, 0.06], roll: 0 },
  lanternStaff: { upper: [-0.24, 0, 0.55], fore: -1.1, aim: [0.17, 1, 0.08], roll: 0 },
  // Rolled so the flat of the blade faces the viewer, as on the sheet.
  sword: { upper: [-0.3, 0, 0.5], fore: -1.4, aim: [0.62, 1, 0.3], roll: -0.5 },
};


const around = (angle: number, rise = 0): V3 => normalize([Math.sin(angle), rise, Math.cos(angle)]);
export function aimAlong(dir: V3, roll = 0): Quat {
  return qMul(qFromTo([0, 1, 0], normalize(dir)), qAxis([0, 1, 0], roll));
}

function track(u: number, keys: [number, number][]): number {
  if (u <= keys[0][0]) return keys[0][1];
  for (let i = 0; i + 1 < keys.length; i++) {
    const [t0, v0] = keys[i], [t1, v1] = keys[i + 1];
    if (u <= t1) return v0 + (v1 - v0) * ease((u - t0) / (t1 - t0));
  }
  return keys[keys.length - 1][1];
}

function trackQ(u: number, keys: [number, Quat][]): Quat {
  if (u <= keys[0][0]) return keys[0][1];
  for (let i = 0; i + 1 < keys.length; i++) {
    const [t0, q0] = keys[i], [t1, q1] = keys[i + 1];
    if (u <= t1) return qSlerp(q0, q1, ease((u - t0) / (t1 - t0)));
  }
  return keys[keys.length - 1][1];
}

function legDrop(pose: Pose, ctx: ClipContext) {
  const [thigh, shin] = legsOf(ctx);
  const knee = qRotate(pose.rot[B.thighL], [0, -thigh, 0]);
  const ankle = qRotate(qMul(pose.rot[B.thighL], pose.rot[B.shinL]), [0, -shin, 0]);
  return thigh + shin + knee[1] + ankle[1];
}

export function hipsHeight(pose: Pose, ctx: ClipContext) {
  const [thigh, shin] = legsOf(ctx);
  return thigh + shin + 0.035 - legDrop(pose, ctx);
}

export function blow(u: number, from: number, to: number, at = 0.55) {
  const x = clamp((u - from) / (to - from), 0, 1);
  return x < at ? at * (x / at) ** 2.2 : at + (1 - at) * (1 - (1 - (x - at) / (1 - at)) ** 2.2);
}

function lunge(pose: Pose, amount: number) {
  rot(pose, B.thighL, -0.45 * amount - 0.05, 0, 0.06);
  rot(pose, B.shinL, 0.2 + 0.35 * amount);
  rot(pose, B.footL, 0.1 * amount);
  rot(pose, B.thighR, 0.42 * amount, 0, -0.06);
  rot(pose, B.shinR, 0.25 + 0.2 * amount);
  rot(pose, B.footR, -0.35 * amount);
}

function swordSweep(pose: Pose, u: number, events: ClipEvents, reach = 1) {
  const draw = track(u, [[0, 0], [0.22, 1], [0.26, 1], [0.4, 0]]);
  const s = blow(u, 0.24, 0.46), back = track(u, [[0.62, 0], [0.9, 1]]);
  // Where the fist is round the shoulder, and how far the blade lags behind it (or leads, after the hit).
  const hand = mix(mix(1.15, 1.95, draw), mix(1.95, -1.2, s), smoothstep(0.23, 0.25, u)) * (1 - back) + 0.9 * back;
  const lag = track(u, [[0, 0.35], [0.22, 0.8], [0.3, 0.6], [0.36, 0.05], [0.46, -0.2], [0.62, -0.15], [0.9, 0]]);
  const rise = track(u, [[0, 0.1], [0.22, 0.4], [0.36, -0.02], [0.5, -0.12], [0.9, 0]]);
  pose.reach = around(hand, rise);
  pose.reachExtent = track(u, [[0, 0.75], [0.22, 0.7], [0.36, 0.93], [0.52, 0.86], [0.9, 0.78]]) * reach;
  pose.reachPole = [0.2, -1, -0.35];
  pose.reachWeight = 1;
  pose.aim = aimAlong(around(hand + lag, rise + track(u, [[0, 0.45], [0.22, 0.55], [0.36, 0.08], [0.52, -0.12], [0.9, 0.3]])));
  pose.aimWeight = 1;
  // The body winds towards the sword side, then turns through with the blow and steps into it.
  const turn = mix(0.5 * draw, mix(0.5, -0.55, s), smoothstep(0.23, 0.25, u)) * (1 - back);
  const step = s * (1 - back);
  lunge(pose, step * 0.85 + draw * 0.15);
  pose.pos[B.root] = [0, -0.035 * draw - 0.04 * step, 0.11 * step - 0.03 * draw];
  rot(pose, B.hips, 0.04 * step, turn * 0.45, 0);
  rot(pose, B.chest, -0.06 * draw + 0.14 * step, turn * 0.6, 0.06 * draw - 0.05 * step);
  rot(pose, B.head, 0.05 * step, -turn * 0.7, 0);
  rot(pose, B.upperArmR, -0.45 * draw + 0.5 * step, 0, -0.3 - 0.25 * step);
  rot(pose, B.foreArmR, -0.9 * draw - 0.5);
  events.trail = track(u, [[0.26, 0], [0.31, 1], [0.44, 1], [0.52, 0]]);
  events.dust = u > 0.35 && u < 0.41 ? 0.45 : 0;
}

function staffSweep(pose: Pose, u: number, events: ClipEvents) {
  const draw = track(u, [[0, 0], [0.22, 1], [0.26, 1], [0.4, 0]]);
  const s = blow(u, 0.24, 0.44, 0.6), back = track(u, [[0.6, 0], [0.9, 1]]);
  const bounce = track(u, [[0.4, 0], [0.46, 1], [0.58, 0]]);
  const hand = mix(mix(1.05, 1.5, draw), mix(1.5, 0.85, s), smoothstep(0.23, 0.25, u)) * (1 - back) + 0.95 * back;
  pose.reach = around(hand, mix(mix(-0.2, 0.55, draw), mix(0.55, -0.45, s), smoothstep(0.23, 0.25, u)) * (1 - back) - 0.3 * back);
  pose.reachExtent = track(u, [[0, 0.78], [0.22, 0.72], [0.4, 0.95], [0.6, 0.9], [0.9, 0.8]]);
  pose.reachPole = [0.35, -1, -0.5];
  pose.reachWeight = 1;
  // The head: up and back over the shoulder, then down across the front to the ground.
  const head = mix(mix(1.4, 2.6, draw), mix(2.6, -0.15, s), smoothstep(0.23, 0.25, u)) * (1 - back) + 0.4 * back;
  const dip = mix(mix(0.9, 0.7, draw), mix(0.7, -0.62, s), smoothstep(0.23, 0.25, u)) * (1 - back) + 0.95 * back + bounce * 0.2;
  pose.aim = aimAlong(around(head, dip));
  pose.aimWeight = 1;
  const turn = mix(0.45 * draw, mix(0.45, -0.35, s), smoothstep(0.23, 0.25, u)) * (1 - back), step = s * (1 - back);
  lunge(pose, step * 0.8);
  pose.pos[B.root] = [0, -0.03 * draw - 0.06 * step, 0.08 * step];
  rot(pose, B.hips, 0.06 * step, turn * 0.4, 0);
  rot(pose, B.chest, -0.1 * draw + 0.24 * step, turn * 0.6, 0);
  rot(pose, B.head, 0.12 * step, -turn * 0.6, 0);
  rot(pose, B.upperArmR, -0.35 * draw + 0.45 * step, 0, -0.3 - 0.2 * step);
  rot(pose, B.foreArmR, -0.7 * draw - 0.45);
  events.trail = track(u, [[0.25, 0], [0.3, 1], [0.42, 1], [0.48, 0]]);
  events.dust = u > 0.39 && u < 0.45 ? 0.8 : 0;
}

function staffCast(pose: Pose, u: number, events: ClipEvents) {
  const raise = track(u, [[0, 0], [0.32, 1], [0.44, 0], [1, 0]]);
  const strike = track(u, [[0.34, 0], [0.46, 1], [0.66, 0.9], [0.88, 0]]);
  lunge(pose, strike * 0.7);
  pose.pos[B.root] = [0, 0.02 * raise - 0.04 * strike, 0.07 * strike];
  rot(pose, B.chest, -0.14 * raise + 0.2 * strike, 0.12 * raise - 0.1 * strike, 0);
  rot(pose, B.head, -0.22 * raise + 0.08 * strike, 0, 0);
  rot(pose, B.upperArmL, -2.75 * raise - 1.5 * strike, 0, 0.18 * raise + 0.2);
  rot(pose, B.foreArmL, -0.12 * raise - 0.2 * strike - 0.25 * (1 - raise));
  rot(pose, B.upperArmR, -1.2 * raise + 0.4 * strike, 0, -0.35 - 0.45 * raise);
  rot(pose, B.foreArmR, -0.8 * raise - 0.3);
  pose.aim = trackQ(u, [[0, pose.aim], [0.3, aimAlong([0.04, 1, -0.3])], [0.46, aimAlong([0.03, 0.42, 1])], [0.66, aimAlong([0.03, 0.48, 1])], [0.9, pose.aim]]);
  events.charge = track(u, [[0.05, 0], [0.36, 1], [0.5, 1], [0.8, 0]]);
  events.burst = u > 0.44 && u < 0.5;
}

export function referenceStrike(pose:Pose,u:number,kind:'sword'|'hammer'|'staff'):ClipEvents {
 const events=noEvents();const h=HOLDS[kind==='sword'?'sword':'crystalStaff'];
 pose.aim=aimAlong(h.aim,h.roll);pose.aimWeight=1;
 if(kind==='sword')swordSweep(pose,u,events);
 else if(kind==='hammer')staffSweep(pose,u,events);else staffCast(pose,u,events);
 return events;
}
